// Peças pequenas que o quadro, a lista, o calendário e a espiada do negócio
// repetem — o dono, o "parado há N dias" e o próximo passo. Uma peça só para
// que as quatro telas digam a mesma coisa do mesmo negócio.
import { Tooltip } from 'antd';
import { ClockCircleOutlined, WarningOutlined, PhoneOutlined } from '@ant-design/icons';
import {
  COR_DO_NIVEL, COR_DO_PASSO, corDoDono, diasSemMovimento, iniciais, nivelParado,
  situacaoDoProximoPasso, textoDeDias, textoDoPasso, LIMITE_ATENCAO, LIMITE_PARADO,
  type NegocioSaude, type ProximoPasso,
} from './saudeNegocio';

/** Bolinha com as iniciais do responsável; cinza tracejado quando não há. */
export function AvatarDono({ nome, tamanho = 22 }: { nome?: string | null; tamanho?: number }) {
  const tem = !!String(nome || '').trim();
  return (
    <Tooltip title={tem ? nome : 'Sem responsável'}>
      <span aria-label={tem ? `Responsável: ${nome}` : 'Sem responsável'} style={{
        width: tamanho, height: tamanho, borderRadius: '50%', flex: 'none',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.round(tamanho * 0.42), fontWeight: 600, letterSpacing: '.02em',
        color: tem ? '#fff' : '#9AA0B4',
        background: tem ? corDoDono(nome) : 'transparent',
        border: tem ? 'none' : '1px dashed #C5C9D6',
      }}>{tem ? iniciais(nome) : '?'}</span>
    </Tooltip>
  );
}

/** "● há 121 dias" na cor do nível. Não aparece em negócio fechado. */
export function SeloParado({ negocio, curto = false }: { negocio: NegocioSaude; curto?: boolean }) {
  const nivel = nivelParado(negocio);
  if (nivel === 'fechado') return null;
  const dias = diasSemMovimento(negocio);
  if (dias == null) return null;
  const cor = COR_DO_NIVEL[nivel];
  const explicacao = nivel === 'parado'
    ? `Parado: ninguém mexe neste negócio ${textoDeDias(dias)} (acima de ${LIMITE_PARADO} dias).`
    : nivel === 'atencao'
      ? `Atenção: último movimento ${textoDeDias(dias)} (acima de ${LIMITE_ATENCAO} dias).`
      : `Em dia: último movimento ${textoDeDias(dias)}.`;
  return (
    <Tooltip title={explicacao}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: cor,
        fontSize: 11, fontWeight: nivel === 'parado' ? 600 : 400, whiteSpace: 'nowrap' }}>
        <ClockCircleOutlined style={{ fontSize: 10 }} />
        {curto ? (dias === 0 ? 'hoje' : `${dias}d`) : textoDeDias(dias)}
      </span>
    </Tooltip>
  );
}

/** O próximo passo: atrasado, para hoje, agendado — ou o aviso de que não há. */
export function SeloPasso({ negocio, passo, curto = false }: {
  negocio: NegocioSaude; passo?: ProximoPasso | null; curto?: boolean;
}) {
  const s = situacaoDoProximoPasso(negocio, passo);
  if (s === 'nao_se_aplica') return null;
  const cor = COR_DO_PASSO[s];
  const texto = textoDoPasso(s, passo);
  const dica = s === 'nenhum'
    ? 'Nenhuma atividade agendada. Negócio aberto sem próximo passo tende a parar.'
    : `${texto}${passo && passo.total > 1 ? ` · ${passo.total} atividades em aberto` : ''}`;
  return (
    <Tooltip title={dica}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: cor, fontSize: 11,
        fontWeight: s === 'vencido' || s === 'hoje' ? 600 : 400, whiteSpace: 'nowrap' }}>
        {s === 'nenhum' ? <WarningOutlined style={{ fontSize: 10 }} /> : <PhoneOutlined style={{ fontSize: 10 }} />}
        {curto && s === 'nenhum' ? 'sem passo' : texto}
      </span>
    </Tooltip>
  );
}
